import {
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    FormControlLabel,
    OutlinedInput,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from "@mui/material";
import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import classes from "./CreateCluster.module.scss";
import MuiAlert from "@mui/material/Alert";
import { exportAutoClusterFile } from "store";
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import StepContent from '@mui/material/StepContent';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import initiate_socket_connect, { getSocket } from "utils/initiate_socket";
import { setSocketStatus } from "../../../store/slices/notificationsSlice";
import Snackbar from "@mui/material/Snackbar";
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { exportAutoClusterFileFromS3 } from "store";

function a11yProps(index) {
    return {
        id: `mui-tab-${index}`,
        'aria-controls': `mui-tabpanel-${index}`,
    };
}

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`mui-tabpanel-${index}`}
            aria-labelledby={`mui-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 2 }}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    );
}

const CreateClusterBox = React.memo(({ onCancel }) => {

    const { loadingExportAutoClusterFileApiResponse, loadingexportAutoClusterFileFromS3Response } = useSelector(
        (state) => state.segmenter
    );
    const dispatch = useDispatch();
    const [file, setFile] = useState(null);
    const [fileName, setFileName] = useState('');
    const [outputFileName, setOutputFileName] = useState('');
    const [userPrompt, setUserPrompt] = useState('');
    const [activeStep, setActiveStep] = useState(0);
    const [error, setError] = useState('');
    const fileRef = useRef(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    const [updatedFile, setUpdatedFile] = useState('');
    const handleUpdatedFileChange = (e) => {
        setUpdatedFile(e.target.value);
    };
    const { inputFileList, loadingImportFiles } = useSelector(
        (state) => state.segmenter
    );
    const [selectedImportFile, setSelectedImportFile] = useState(null);
    const [value, setValue] = React.useState(0);

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const handleRadioChange = (event) => {
        setSelectedImportFile(event.target.value);
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarOpen(false);
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile && selectedFile.type === 'text/csv') {
            setFile(selectedFile);
            setFileName(selectedFile.name);
            setError('');
        } else {
            setError('Please select a valid CSV file.');
        }
    };

    const handleUpload = async () => {
        if (value === 0) {
            try {
                dispatch(exportAutoClusterFile({ file: file })).unwrap()
                    .then((data) => {
                        setActiveStep(1);
                        setError('');
                    })
                    .catch((err) => {
                        setError(err.message);
                    });

            } catch (error) {
                setError(error.message);
            } finally {
            }

        }
        else {
            if (!selectedImportFile) {
                setError('No file selected');
                return;
            }
            try {
                dispatch(exportAutoClusterFileFromS3({ inputFileName: selectedImportFile, renamedFile: updatedFile.concat(".csv") })).unwrap()
                    .then((data) => {
                        setFileName(updatedFile.concat(".csv"));
                        setActiveStep(1);
                        setError('');
                    })
                    .catch((err) => {
                        setError(err.message);
                    });

            } catch (error) {
                setError(error.message);
            } finally {
            }
        }
    };

    const handleReset = () => {
        setActiveStep(0);
        setOutputFileName('');
        setError('');
        setFile(null);
        setFileName('');
        setUserPrompt('');
        fileRef.current && (fileRef.current.value = '')
    };

    const handleFileNameChange = (e) => {
        setOutputFileName(e.target.value)
    }

    const handleUserPromptChange = (e) => {
        setUserPrompt(e.target.value);
    }

    const createCluster = (e) => {

        let payload = {
            inputFileName: fileName,
            outputFileName: outputFileName.concat(".csv"),
            userprompt: userPrompt
        }

        initiate_socket_connect(payload);
        let socket = getSocket()

        dispatch(setSocketStatus(socket.url));
        setSnackbarOpen(true);
        setActiveStep(prev => prev + 1);
    }

    const onDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const onDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const onDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const onDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const files = e.dataTransfer?.files;
        if (!files || files.length === 0) return;

        const fileList = files;
        try {
            const dt = new DataTransfer();
            for (let i = 0; i < fileList.length; i++) dt.items.add(fileList[i]);
            if (fileRef.current) {
                fileRef.current.files = dt.files;
                fileRef.current.dispatchEvent(new Event('change', { bubbles: true }));
            }
        } catch (err) {

            handleFileChange?.({ target: { files: fileList } });
            return;
        }
        handleFileChange?.({ target: { files: fileList } });
    };

    const steps = [
        {
            label: 'Upload CSV File',
            description: (
                <><section className={classes.section}>
                    <section className={classes.header}>
                        <Typography className={classes.label}>Select a file below to begin uploading data</Typography>
                        <Typography className={classes.highlightLabel} variant="caption">* No spaces or Special characters allowed.</Typography>
                    </section>
                    <Box sx={{ width: '100%' }}>
                        <Tabs
                            value={value}
                            onChange={handleChange}
                            aria-label="Example tabs"
                            textColor="primary"
                            indicatorColor="primary"
                        >
                            <Tab label="Upload New File" {...a11yProps(0)} />
                            <Tab label="Upload from s3" {...a11yProps(1)} />
                        </Tabs>
                        <TabPanel value={value} index={0}>
                            <section className={classes.formSection}>
                                <div className={classes.formContent}>
                                    <h3>Upload CSV File</h3>
                                    <label htmlFor="images" className={classes.dropContainer} id="dropcontainer"
                                        onDragEnter={onDragEnter}
                                        onDragOver={onDragOver}
                                        onDragLeave={onDragLeave}
                                        onDrop={onDrop}>
                                        <span className={classes.dropTitle}>Drop files here</span>
                                        or
                                        <div className={classes.inputContainer}>
                                            <input ref={fileRef} type="file" accept=".csv" onChange={handleFileChange} />
                                        </div>
                                    </label>
                                    {fileName && <p>Selected file: <strong>{fileName}</strong></p>}
                                    {fileName && <Chip label={fileName} onDelete={handleReset} onClick={() => { }} />}
                                </div>
                            </section>
                        </TabPanel>
                        <TabPanel value={value} index={1} keepMounted>
                            <section className={classes.formSection}>
                                <FormControl component="fieldset" className={classes.formControlSection}>
                                    {loadingImportFiles ? (
                                        <div className={classes.importLoader}>
                                            <CircularProgress size="20px" />
                                        </div>
                                    ) : (
                                        <RadioGroup onChange={handleRadioChange} value={selectedImportFile}>
                                            {inputFileList.map((file, index) => (
                                                <FormControlLabel
                                                    key={index}
                                                    value={file}
                                                    control={<Radio />}
                                                    label={file}
                                                />
                                            ))}
                                        </RadioGroup>
                                    )}
                                </FormControl>
                            </section>
                            <section className={classes.fileSection}>
                                <Typography className={classes.label}>Please rename the file:</Typography>
                                <TextField
                                    id="outlined-basic"
                                    label=""
                                    variant="outlined"
                                    placeholder="Updated File Name"
                                    value={updatedFile}
                                    onChange={handleUpdatedFileChange}
                                    className={classes.inputBoxStyle}
                                    autoComplete="off"
                                />
                            </section>
                        </TabPanel>
                    </Box>
                </section></>
            ),
        },
        {
            label: 'Additional Information',
            description: (
                <section className={classes.section}>
                    <section>
                        <Typography className={classes.label}>Please provide prompt to the LLM for Creating Segments:</Typography>
                        <textarea
                            value={userPrompt}
                            onChange={handleUserPromptChange}
                            rows={4}
                            className={`${classes.userPromptStyle} ${classes.userPromptFullWidth}`}
                            placeholder="Enter additional info here..."
                        />
                    </section>
                </section>
            ),
        },
        {
            label: 'Enter Output File Name',
            description:
                (<section className={classes.section}>
                    <section className={classes.formSection}>
                        <OutlinedInput
                            placeholder="Output File Name"
                            value={outputFileName}
                            onChange={handleFileNameChange}
                            className={classes.inputBoxStyle}
                        />
                    </section>
                </section>),
        },
        {
            label: 'Run Segment/Clustering',
            description: (<section className={classes.section}>
                <section className={classes.header}>
                    <Typography className={classes.title}>Run Segment</Typography>
                </section>
                <section className={classes.formSection}>
                    <p>Please click on the Create Cluster button below.</p>
                </section>
            </section>),
        },
    ];


    const handleNext = async () => {
        if (activeStep === 0) {
            if (!file && value === 0) {
                setError("Please select a CSV file..");
                return;
            }
            try {
                await handleUpload();
            } catch (err) {
                setError(err?.message || "Upload failed");
            }
        } else if (activeStep === 1) {
            setActiveStep((prev) => prev + 1);
            setError('');
        } else if (activeStep === 2) {
            if (outputFileName === '') {
                setError("File Name can't be Empty");
            } else {
                setActiveStep((prev) => prev + 1);
                setError('');
            }
        } else if (activeStep === 3) {
            createCluster();
        }
    };

    return (

        <Box className={classes.body}>
            <section className={classes.sectionHeader} >
                <IconButton onClick={onCancel} color="primary">
                    <CloseIcon />
                </IconButton>
            </section>
            <Box>
                <Stepper activeStep={activeStep} orientation="vertical">
                    {steps.map((step, index) => (
                        <Step key={step.label}>
                            <StepLabel
                                optional={
                                    index === steps.length - 1 ? (
                                        <Typography variant="caption">Last step</Typography>
                                    ) : null
                                }
                            >
                                {step.label}
                            </StepLabel>
                            <StepContent>
                                <Typography>{step.description}</Typography>
                                <Box sx={{ mb: 2 }}>
                                    {index === 0 && <Button
                                        variant="contained"
                                        onClick={handleNext}
                                        sx={{ mt: 1, mr: 1 }}
                                    >
                                        {loadingExportAutoClusterFileApiResponse || loadingexportAutoClusterFileFromS3Response ? 'Uploading....' : 'Upload'}
                                    </Button>}

                                    {index === 1 && <Button
                                        variant="contained"
                                        onClick={handleNext}
                                        sx={{ mt: 1, mr: 1 }}
                                    >
                                        Continue
                                    </Button>}

                                    {index === 2 && <Button
                                        variant="contained"
                                        onClick={handleNext}
                                        sx={{ mt: 1, mr: 1 }}
                                    >
                                        Continue
                                    </Button>}

                                    {index === 3 && <Button
                                        variant="contained"
                                        onClick={handleNext}
                                        sx={{ mt: 1, mr: 1 }}
                                    >
                                        Create Cluster
                                    </Button>}

                                    {error !== '' && <MuiAlert className={classes.muiAlert} severity="error">
                                        {error}
                                    </MuiAlert>}
                                </Box>
                            </StepContent>
                        </Step>
                    ))}
                </Stepper>
                {activeStep === steps.length && (
                    <Paper square elevation={0} sx={{ p: 3 }}>
                        <Typography>All steps are complete — your cluster will be created shortly. Click the Reset button to create a new cluster.</Typography>
                        <Typography sx={{ mt: 2, mb: 1 }}><strong>Additional Info Provided:</strong> {userPrompt}</Typography>
                        <Button onClick={handleReset} sx={{ mt: 1, mr: 1 }}>
                            Reset & Upload New File
                        </Button>
                        <Snackbar
                            open={snackbarOpen}
                            autoHideDuration={5000}
                            onClose={handleSnackbarClose}
                            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                            <MuiAlert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
                                <Typography color="success.main" sx={{ mt: 1 }}>Job started, you will be notified once the job completed</Typography>
                            </MuiAlert>
                        </Snackbar>
                    </Paper>
                )}
            </Box>
        </Box>
    );
});

export default CreateClusterBox;
